import type { ReactNode } from "react";
import { forwardRef } from "react";
import classnames from "classnames";

export interface InputProps {
  [key: string]: any;
  className?: string;
  customOpacity?: string;
  disabled?: boolean;
  focusRequired?: boolean;
  hideBorder?: boolean;
  noPadding?: boolean;
  otherClassName?: string;
  prefix?: ReactNode;
  prefixWrapperClassName?: string;
  required?: boolean;
  suffix?: ReactNode;
  suffixInnerClassName?: string;
  suffixWrapperClassName?: string;
  variant?: "text" | "textarea";
  wrapperClassName?: string;
}

export type InputOrArea = HTMLInputElement | HTMLTextAreaElement;

export const Input = forwardRef<InputOrArea, InputProps>(
  (
    {
      className = "form-control",
      wrapperClassName,
      prefixWrapperClassName,
      suffixWrapperClassName,
      suffixInnerClassName,
      otherClassName,
      hideBorder = false,
      noPadding = false,
      prefix,
      suffix,
      variant = "text",
      disabled = false,
      focusRequired = true,
      customOpacity = "70",
      ...rest
    },
    ref,
  ) => {
    const Input: any = variant === "textarea" ? "textarea" : "input";
    const calculatedOpacity = disabled
      ? Number(customOpacity) / 100
      : undefined;

    return (
      <div
        className={classnames(
          "text-default flex w-full rounded-lg ring-current",
          {
            "focus-within:ring-1": focusRequired,
          },
          wrapperClassName,
        )}
      >
        {Boolean(prefix) && (
          <span
            className={classnames(
              "form-control__prefix disabled:bg-secondary disabled:cursor-not-allowed disabled:opacity-70",
              {
                "cursor-not-allowed": disabled,
              },
              prefixWrapperClassName,
            )}
            style={{ opacity: calculatedOpacity }}
          >
            {prefix}
          </span>
        )}
        <Input
          ref={ref}
          className={classnames(
            "placeholder:text-light disabled:bg-secondary placeholder:opacity-100 disabled:cursor-not-allowed disabled:opacity-70",
            {
              "with-prefix": Boolean(prefix),
              "with-suffix": Boolean(suffix),
              "form-control--no-padding": noPadding,
              "form-control--no-border": hideBorder,
              "cursor-not-allowed": disabled,
            },
            className,
            otherClassName,
          )}
          disabled={disabled}
          style={{ opacity: calculatedOpacity }}
          {...rest}
        />
        {Boolean(suffix) && (
          <div
            className={classnames(
              "form-control__suffix disabled:bg-secondary disabled:cursor-not-allowed disabled:opacity-70",
              {
                "cursor-not-allowed": disabled,
              },
              suffixWrapperClassName,
            )}
            style={{ opacity: calculatedOpacity }}
          >
            <span className={suffixInnerClassName}>{suffix}</span>
          </div>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
